import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../Modal';
import { getOpenInCrmHref } from '../../OpenInCrmButton/OpenInCrmButton';
import { dropRestOfDoubleClick } from '../../../utils/dropRestOfDoubleClick';
import { formatStoredPhoneNumber } from '../../../utils';
import { CheckIcon, ExternalIcon, PlusIcon } from '../icons';

/**
 * Kore's possible duplicates for the owners a save would file. Each answer is a
 * card: the owner as a new investor, or one of Kore's leads. One owner: the
 * answer saves. A bulk add: each property is answered, then all go in.
 */

const STRENGTH_LABELS = { definite: 'Likely the same', possible: 'Possible' };

const MATCHED_LABELS = {
    email: 'Same email',
    cell: 'Same cell',
    phone: 'Same phone',
    name: 'Same name',
    nickname: 'Nickname',
    company: 'Same company',
    city: 'Same city',
    address: 'Same address',
    lightbox: 'Filed from this LightBox person',
};

// Shown with the value: the shared number or address is the evidence.
const MATCHED_WITH_VALUE = ['email', 'cell', 'phone', 'address', 'nickname'];

const NUMBER_FIELDS = ['cell', 'phone'];

/** What two records share, each detail ticked: a Kore lead's here, the record on file's on the owner card. */
export function MatchEvidence({ matched }) {
    if (!matched || !matched.length) return null;

    return (
        <div className="LeadMatches-evidence">
            {matched.map((each) => (
                <span key={each.field} className="LeadMatches-ev">
                    <CheckIcon />
                    {MATCHED_LABELS[each.field] || each.field}
                    {MATCHED_WITH_VALUE.indexOf(each.field) !== -1 && each.value && (
                        <React.Fragment> <b>{NUMBER_FIELDS.indexOf(each.field) !== -1 ? formatStoredPhoneNumber(each.value) : each.value}</b></React.Fragment>
                    )}
                </span>
            ))}
        </div>
    );
}

// Long enough to see an answer land before the window moves on.
const ANSWER_BEAT_MS = 320;

function plural(count, one, many) {
    return count + ' ' + (count === 1 ? one : many);
}

function initials(name) {
    return String(name)
        .split(/[^A-Za-z0-9]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('');
}

function ChoiceButton({ isChosen, onClick, children }) {
    return (
        <button type="button" className={'Btn LeadMatches-choose' + (isChosen ? ' Btn--primary' : '')} onClick={onClick}>
            {isChosen ? <React.Fragment><CheckIcon /> Selected</React.Fragment> : children}
        </button>
    );
}

function OwnerOption({ adding, isChosen, onChoose }) {
    return (
        <div className={'LeadMatches-option LeadMatches-option--owner' + (isChosen ? ' LeadMatches-option--on' : '')}>
            <div className="LeadMatches-avatar" aria-hidden="true">{initials(adding.name || '')}</div>
            <div className="LeadMatches-optionMain">
                <div className="LeadMatches-name">{adding.name}</div>
                {adding.lines.filter(Boolean).map((line) => (
                    <div key={line} className="LeadMatches-meta">{line}</div>
                ))}
            </div>
            <div className="LeadMatches-optionActions">
                <ChoiceButton isChosen={isChosen} onClick={onChoose}>
                    <PlusIcon /> Add as new investor
                </ChoiceButton>
            </div>
        </div>
    );
}

function MatchOption({ match, isChosen, onChoose }) {
    const name = match.name || 'Unnamed lead';
    const where = [match.city, match.state].filter(Boolean).join(', ');
    const sub = [
        match.company,
        where,
        match.lead_owner && 'Lead owner ' + match.lead_owner,
        match.status,
    ].filter(Boolean).join(' · ');

    return (
        <div className={'LeadMatches-option LeadMatches-option--match' + (isChosen ? ' LeadMatches-option--on' : '')}>
            <div className="LeadMatches-avatar" aria-hidden="true">{initials(name)}</div>
            <div className="LeadMatches-optionMain">
                <div className="LeadMatches-nameRow">
                    <span className="LeadMatches-name">{name}</span>
                    <span className={'OwnerPill ' + (match.strength === 'definite' ? 'OwnerPill--likely' : 'OwnerPill--onfile')}>
                        {STRENGTH_LABELS[match.strength] || match.label}
                    </span>
                    <span className={'OwnerPill ' + (match.investor_id ? 'OwnerPill--match' : 'OwnerPill--onfile')}>
                        {match.investor_id ? 'In iCLIMB' : 'Kore only'}
                    </span>
                </div>
                {sub && <div className="LeadMatches-meta">{sub}</div>}
                <MatchEvidence matched={match.matched} />
            </div>
            <div className="LeadMatches-optionActions">
                <ChoiceButton isChosen={isChosen} onClick={onChoose}>Use this lead</ChoiceButton>
                <a
                    className="OwnerOpen-link LeadMatches-koreLink"
                    href={getOpenInCrmHref(match.lead_id, 'lead')}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Open in Kore <ExternalIcon />
                </a>
            </div>
        </div>
    );
}

function Choices({ item, decision, direction, onUse, onCreateNew }) {
    const shown = item.matches.length;
    const total = Math.max(item.total || 0, shown);

    return (
        <div className={'LeadMatches-choices' + (direction ? ' LeadMatches-choices--' + direction : '')}>
            <div className="LeadMatches-group">
                <div className="LeadMatches-label">Owner you’re adding</div>
                <OwnerOption
                    adding={item.adding}
                    isChosen={Boolean(decision && decision.createNew)}
                    onChoose={() => onCreateNew(item.key)}
                />
            </div>
            <div className="LeadMatches-group">
                <div className="LeadMatches-label">Already in Kore · {plural(total, 'possible match', 'possible matches')}</div>
                {item.matches.map((match) => (
                    <MatchOption
                        key={match.lead_id}
                        match={match}
                        isChosen={Boolean(decision && decision.koreLeadId === match.lead_id)}
                        onChoose={() => onUse(item.key, match)}
                    />
                ))}
                {total > shown && (
                    <div className="LeadMatches-note">
                        Showing the {shown} strongest of {total} matches.
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * An answer moves the bulk window on to the next property, whose buttons then
 * sit under the pointer: the second click of a double click answered it too.
 */
function answerOnce(answer) {
    return (...args) => {
        dropRestOfDoubleClick();
        answer(...args);
    };
}

/**
 * The property a bulk window shows. It follows activeKey, but a property just
 * answered stays on screen for a beat, so the user sees the answer land before
 * the next one slides in.
 */
function useShownKey(activeKey, decisions, keys) {
    const [shown, setShown] = useState({ key: activeKey, direction: null });
    const seenAnswer = useRef(decisions[activeKey]);
    const order = useRef(keys);

    order.current = keys;

    useEffect(() => {
        const answer = decisions[shown.key];
        const justAnswered = Boolean(answer) && answer !== seenAnswer.current;

        seenAnswer.current = answer;

        if (activeKey === shown.key) return undefined;

        const direction = order.current.indexOf(activeKey) > order.current.indexOf(shown.key) ? 'next' : 'prev';
        const move = () => setShown({ key: activeKey, direction });

        if (!justAnswered) {
            move();

            return undefined;
        }

        const timer = setTimeout(move, ANSWER_BEAT_MS);

        return () => clearTimeout(timer);
    }, [activeKey, decisions, shown.key]);

    return shown;
}

function sidebarStatus(item, decision) {
    if (!decision) return 'Choose';
    if (decision.createNew) return 'New investor';

    const match = item.matches.filter((each) => each.lead_id === decision.koreLeadId)[0];

    return 'Use ' + (match ? match.name : 'Kore lead ' + decision.koreLeadId);
}

export default function LeadMatchesModal({
    mode,
    status,
    error,
    items,
    activeKey,
    decisions,
    total,
    notice,
    isSaving,
    confirmLabel,
    savingLabel,
    listLabel = 'Properties to decide',
    onActivate,
    onUse,
    onCreateNew,
    onConfirm,
    onRetry,
    onCancel,
}) {
    const isBulk = mode === 'bulk';
    const shown = useShownKey(activeKey, decisions, items.map((item) => item.key));
    const active = items.filter((item) => item.key === shown.key)[0] || items[0];
    const decided = items.filter((item) => decisions[item.key]).length;
    const [choosing, setChoosing] = useState(null);
    const saveTimer = useRef(null);
    const activeTab = useRef(null);
    const pane = useRef(null);

    // Once the add is on its way there is nothing left to call off.
    const cancel = () => {
        if (!isSaving) onCancel();
    };

    // Escape is Cancel, as clicking the backdrop is: nothing is saved either way.
    useEffect(() => {
        const onKey = (event) => {
            if (event.key === 'Escape' && !isSaving) onCancel();
        };

        window.addEventListener('keydown', onKey);

        return () => window.removeEventListener('keydown', onKey);
    }, [onCancel, isSaving]);

    useEffect(() => () => dropRestOfDoubleClick(), []);

    // Closing the window inside the beat drops the answer: nothing is saved.
    useEffect(() => () => clearTimeout(saveTimer.current), []);

    useEffect(() => {
        if (activeTab.current) activeTab.current.scrollIntoView({ block: 'nearest' });
        if (pane.current) pane.current.scrollTop = 0;
    }, [shown.key]);

    // One owner: the answer shows for a beat, then saves.
    const chooseThenSave = (decision, save) => {
        if (choosing) return;

        setChoosing(decision);
        saveTimer.current = setTimeout(save, ANSWER_BEAT_MS);
    };

    const titles = {
        error: 'Couldn’t check Kore',
        clear: 'No match in Kore',
        loading: 'Checking Kore',
    };
    const title = titles[status] || (isBulk ? 'Some owners may already be in Kore' : 'This owner may already be in Kore');

    let body;

    if (status === 'clear') {
        body = (
            <div className="LeadMatches-message">
                Nothing in Kore looks like this owner, so the save files a new investor for them.
            </div>
        );
    } else if (status === 'error') {
        body = (
            <div className="LeadMatches-message">
                Kore didn{'’'}t answer, so {isBulk ? 'these owners' : 'this owner'} couldn{'’'}t be checked for
                duplicates. Nothing was saved.
                {error && <div className="LeadMatches-detail">{error}</div>}
            </div>
        );
    } else if (status === 'loading') {
        body = (
            <div className="LeadMatches-message">
                <span className="Btn-spinner LeadMatches-spinner" /> Looking for leads that may already be this owner…
            </div>
        );
    } else if (isBulk) {
        body = (
            <div className="LeadMatches-split">
                <div className="LeadMatches-sidebar">
                    <div className="LeadMatches-listLabel">{listLabel}</div>
                    <div className="LeadMatches-list" role="tablist" aria-label={listLabel}>
                        {items.map((item) => {
                            const isOn = item.key === active.key;

                            return (
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={isOn}
                                    key={item.key}
                                    ref={isOn ? activeTab : null}
                                    className={'LeadMatches-item'
                                        + (isOn ? ' LeadMatches-item--on' : '')
                                        + (decisions[item.key] ? ' LeadMatches-item--done' : '')}
                                    onClick={() => onActivate(item.key)}
                                >
                                    <span className="LeadMatches-itemTitle">{item.title}</span>
                                    <span className="LeadMatches-itemSub">{item.subtitle}</span>
                                    <span className="LeadMatches-itemStatus">
                                        <span className="LeadMatches-dot" />
                                        {sidebarStatus(item, decisions[item.key])}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="LeadMatches-pane" ref={pane}>
                    <Choices
                        key={active.key}
                        item={active}
                        decision={decisions[active.key]}
                        direction={shown.direction}
                        onUse={answerOnce(onUse)}
                        onCreateNew={answerOnce(onCreateNew)}
                    />
                </div>
            </div>
        );
    } else {
        body = (
            <div className="LeadMatches-pane" ref={pane}>
                <Choices
                    item={active}
                    decision={choosing || decisions[active.key]}
                    onUse={(key, match) => chooseThenSave({ koreLeadId: match.lead_id }, () => onUse(key, match))}
                    onCreateNew={(key) => chooseThenSave({ createNew: true }, () => onCreateNew(key))}
                />
            </div>
        );
    }

    return (
        <Modal show onClose={cancel} className={'LeadMatches' + (isBulk && status === 'ready' ? ' LeadMatches--bulk' : '')}>
            <div className="LeadMatches-dialog" role="dialog" aria-modal="true" aria-labelledby="LeadMatches-title">
                <div className="LeadMatches-head">
                    <div className="LeadMatches-headText">
                        <div id="LeadMatches-title" className="LeadMatches-title">{title}</div>
                    </div>
                    <button type="button" className="LeadMatches-close" onClick={cancel} aria-label="Close">×</button>
                </div>

                <div className="LeadMatches-body">{body}</div>

                <div className="LeadMatches-foot">
                    {status === 'ready' && isBulk && (
                        <div className={'LeadMatches-progress' + (notice ? ' LeadMatches-progress--warn' : '')}>
                            <span>{notice || decided + ' of ' + items.length + ' decided'}</span>
                            <span className="LeadMatches-bar">
                                <i style={{ width: Math.round((decided / items.length) * 100) + '%' }} />
                            </span>
                        </div>
                    )}
                    {status === 'ready' && !isBulk && (
                        <div className="LeadMatches-progress">Nothing is saved until you choose.</div>
                    )}
                    {status !== 'ready' && <div className="ParcelMap-spacer" />}
                    <button type="button" className="Btn" onClick={onCancel} disabled={isSaving}>Cancel</button>
                    {status === 'error' && (
                        <button type="button" className="Btn Btn--primary" onClick={onRetry}>Try again</button>
                    )}
                    {status === 'clear' && (
                        <button type="button" className="Btn Btn--primary" onClick={onConfirm}>{confirmLabel}</button>
                    )}
                    {status === 'ready' && isBulk && (
                        <button type="button" className="Btn Btn--primary" onClick={onConfirm} disabled={isSaving}>
                            {isSaving && <span className="Btn-spinner" />}
                            {isSaving
                                ? savingLabel || 'Adding ' + total + '…'
                                : confirmLabel || 'Add ' + plural(total, 'property', 'properties')}
                        </button>
                    )}
                </div>
            </div>
        </Modal>
    );
}
